<template>
  <div>
    <EnvironmentUnavailable v-if="envUnavailable" class="mb-6" />

    <!-- Endpoints -->
    <div class="flex items-center justify-between mb-6">
      <p class="text-sm text-gray-600 max-w-2xl">
        Youdégo appelle vos URL en HTTP POST à chaque événement, avec une signature
        <code class="text-xs bg-gray-100 px-1.5 py-0.5 rounded">X-Youdego-Signature</code>.
        Un envoi qui échoue est rejoué automatiquement (1 min, 5 min, 30 min, 2 h, 12 h).
      </p>
      <button
        @click="openCreate"
        :disabled="authStore.isSuspended"
        class="px-4 py-2 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50 flex items-center gap-2 flex-shrink-0"
      >
        <Icon icon="solar:add-circle-bold" class="w-4 h-4" />
        Nouvel endpoint
      </button>
    </div>

    <div class="space-y-4 mb-8">
      <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
        <div class="h-4 bg-gray-100 rounded w-1/3"></div>
      </div>

      <div
        v-else-if="endpoints.length === 0"
        class="bg-white rounded-xl border border-gray-200 p-12 text-center"
      >
        <Icon icon="solar:bell-bold" class="w-10 h-10 text-gray-300 mx-auto mb-3" />
        <p class="text-gray-500">Aucun endpoint configuré dans cet environnement.</p>
      </div>

      <div
        v-else
        v-for="ep in endpoints"
        :key="ep.id"
        class="bg-white rounded-xl border border-gray-200 shadow-sm p-5"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 mb-2">
              <span
                :class="[
                  'px-2 py-0.5 rounded-full text-xs font-semibold',
                  ep.active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'
                ]"
              >
                {{ ep.active ? 'Actif' : 'Désactivé' }}
              </span>
              <code class="text-sm text-gray-900 font-mono break-all">{{ ep.url }}</code>
            </div>

            <p class="text-xs text-gray-500 mb-3">
              {{ ep.events.length === 0 ? 'Tous les événements' : ep.events.join(', ') }}
            </p>

            <div class="flex items-center gap-2">
              <span class="text-xs text-gray-500">Secret de signature :</span>
              <code v-if="visibleSecrets[ep.id]" class="text-xs font-mono bg-gray-100 px-2 py-1 rounded break-all">
                {{ ep.secret }}
              </code>
              <code v-else class="text-xs font-mono bg-gray-100 px-2 py-1 rounded">whsec_••••••••</code>
              <button
                @click="visibleSecrets[ep.id] = !visibleSecrets[ep.id]"
                class="text-xs text-[#8C0004] hover:underline"
              >
                {{ visibleSecrets[ep.id] ? 'Masquer' : 'Afficher' }}
              </button>
            </div>
          </div>

          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              @click="testEndpoint(ep)"
              :disabled="testingId === ep.id"
              class="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              {{ testingId === ep.id ? 'Envoi...' : 'Tester' }}
            </button>
            <button
              @click="toggleActive(ep)"
              class="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50"
            >
              {{ ep.active ? 'Désactiver' : 'Activer' }}
            </button>
            <button
              @click="remove(ep)"
              class="px-3 py-1.5 border border-[#8C0004] text-[#8C0004] rounded-lg text-xs font-medium hover:bg-red-50"
            >
              Supprimer
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Journal des envois -->
    <h3 class="font-semibold text-gray-900 mb-4">Derniers envois</h3>
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Événement</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Statut</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Tentatives</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Réponse</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
              <th class="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr v-if="deliveries.length === 0">
              <td colspan="6" class="px-6 py-12 text-center text-gray-500">
                Aucun envoi pour l'instant.
              </td>
            </tr>
            <tr v-else v-for="w in deliveries" :key="w.id" class="hover:bg-gray-50">
              <td class="px-6 py-4">
                <code class="text-sm text-gray-900">{{ w.eventType }}</code>
              </td>
              <td class="px-6 py-4">
                <span :class="['px-3 py-1 rounded-full text-xs font-semibold', deliveryStatusClass(w.status)]">
                  {{ deliveryStatusLabel(w.status) }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600">{{ w.attemptCount }}</td>
              <td class="px-6 py-4 text-sm text-gray-600">
                <span v-if="w.lastStatusCode">HTTP {{ w.lastStatusCode }}</span>
                <span v-else-if="w.lastError" class="text-red-600 text-xs">{{ w.lastError }}</span>
                <span v-else>—</span>
              </td>
              <td class="px-6 py-4 text-sm text-gray-600">{{ formatDate(w.createdAt) }}</td>
              <td class="px-6 py-4">
                <button
                  v-if="w.status !== 'SUCCESS'"
                  @click="retry(w)"
                  class="px-3 py-1 border border-gray-300 rounded text-xs font-medium hover:bg-gray-50"
                >
                  Renvoyer
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal création -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      @click.self="showModal = false"
    >
      <div class="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 class="text-lg font-bold text-gray-900">Nouvel endpoint webhook</h3>
          <button @click="showModal = false" class="text-gray-400 hover:text-gray-600">
            <Icon icon="solar:close-circle-bold" class="w-6 h-6" />
          </button>
        </div>
        <form @submit.prevent="create" class="p-6 space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">URL de destination</label>
            <input
              v-model="form.url"
              type="url"
              required
              :placeholder="modeStore.isLive ? 'https://votre-domaine.com/webhooks/youdego' : 'https://…'"
              class="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#8C0004]"
            />
            <p v-if="modeStore.isLive" class="text-xs text-gray-500 mt-2">
              En production, seules les URL HTTPS sont acceptées.
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Événements souscrits
            </label>
            <p class="text-xs text-gray-500 mb-3">
              Ne rien cocher revient à tout recevoir.
            </p>
            <div class="space-y-2 max-h-52 overflow-y-auto border border-gray-200 rounded-lg p-3">
              <label
                v-for="evt in EVENT_TYPES"
                :key="evt"
                class="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  :value="evt"
                  v-model="form.events"
                  class="rounded border-gray-300 text-[#8C0004] focus:ring-[#8C0004]"
                />
                <code class="text-xs text-gray-700">{{ evt }}</code>
              </label>
            </div>
          </div>

          <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-sm text-red-600">{{ error }}</p>
          </div>

          <div class="flex gap-3 pt-2">
            <button
              type="button"
              @click="showModal = false"
              class="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="!form.url || saving"
              class="flex-1 px-4 py-2.5 bg-[#8C0004] text-white rounded-lg text-sm font-medium hover:bg-red-900 disabled:opacity-50"
            >
              {{ saving ? 'Création...' : 'Créer' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Icon } from '@iconify/vue'
import { apiFor, isEnvironmentUnauthorized } from '../services/api'
import EnvironmentUnavailable from '../components/EnvironmentUnavailable.vue'
import { useModeStore } from '../store/mode'
import { useAuthStore } from '../store/auth'
import { useNotification } from '../composables/useNotification'
import { EVENT_TYPES } from '../services/events'

const modeStore = useModeStore()
const authStore = useAuthStore()
const notify = useNotification()

const endpoints = ref<any[]>([])
const deliveries = ref<any[]>([])
const loading = ref(true)
const envUnavailable = ref(false)
const showModal = ref(false)
const saving = ref(false)
const error = ref('')
const testingId = ref('')
const visibleSecrets = ref<Record<string, boolean>>({})

const form = ref({ url: '', events: [] as string[] })

onMounted(() => {
  load()
  loadDeliveries()
})

async function load() {
  loading.value = true
  try {
    const { data } = await apiFor(modeStore.mode).get('/partner-portal/webhook-endpoints')
    endpoints.value = data.endpoints || []
  } catch (err) {
    envUnavailable.value = isEnvironmentUnauthorized(err)
    console.error('Erreur chargement endpoints:', err)
    endpoints.value = []
  } finally {
    loading.value = false
  }
}

async function loadDeliveries() {
  try {
    const { data } = await apiFor(modeStore.mode).get('/partner-portal/webhook-deliveries', {
      params: { limit: 20 },
    })
    deliveries.value = data.deliveries || []
  } catch (err) {
    console.error('Erreur chargement journal:', err)
    deliveries.value = []
  }
}

function openCreate() {
  form.value = { url: '', events: [] }
  error.value = ''
  showModal.value = true
}

async function create() {
  saving.value = true
  error.value = ''
  try {
    await apiFor(modeStore.mode).post('/partner-portal/webhook-endpoints', form.value)
    showModal.value = false
    notify.success('Endpoint créé')
    await load()
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Erreur lors de la création'
  } finally {
    saving.value = false
  }
}

async function toggleActive(ep: any) {
  try {
    await apiFor(modeStore.mode).patch(`/partner-portal/webhook-endpoints/${ep.id}`, {
      active: !ep.active,
    })
    await load()
  } catch (err: any) {
    notify.error(err.response?.data?.message || 'Erreur')
  }
}

async function remove(ep: any) {
  if (!window.confirm(`Supprimer l'endpoint ${ep.url} ? Vous ne recevrez plus ses événements.`)) {
    return
  }
  try {
    await apiFor(modeStore.mode).delete(`/partner-portal/webhook-endpoints/${ep.id}`)
    notify.success('Endpoint supprimé')
    await load()
  } catch (err: any) {
    notify.error(err.response?.data?.message || 'Erreur')
  }
}

async function testEndpoint(ep: any) {
  testingId.value = ep.id
  try {
    const { data } = await apiFor(modeStore.mode).post(
      `/partner-portal/webhook-endpoints/${ep.id}/test`
    )
    if (data.delivery?.status === 'SUCCESS') {
      notify.success(data.message)
    } else {
      notify.error(data.message)
    }
    await loadDeliveries()
  } catch (err: any) {
    notify.error(err.response?.data?.message || 'Erreur lors du test')
  } finally {
    testingId.value = ''
  }
}

async function retry(w: any) {
  try {
    const { data } = await apiFor(modeStore.mode).post(
      `/partner-portal/webhook-deliveries/${w.id}/retry`
    )
    if (data.delivery?.status === 'SUCCESS') {
      notify.success('Renvoi réussi')
    } else {
      notify.warning(`Renvoi échoué : ${data.delivery?.lastError || 'erreur inconnue'}`)
    }
    await loadDeliveries()
  } catch (err: any) {
    notify.error(err.response?.data?.message || 'Erreur')
  }
}

function deliveryStatusLabel(status: string): string {
  return (
    {
      PENDING: 'En attente',
      SUCCESS: 'Reçu',
      FAILED: 'Échec (rejeu prévu)',
      EXHAUSTED: 'Abandonné',
    } as Record<string, string>
  )[status] || status
}

function deliveryStatusClass(status: string): string {
  return (
    {
      PENDING: 'bg-gray-100 text-gray-800',
      SUCCESS: 'bg-green-100 text-green-800',
      FAILED: 'bg-amber-100 text-amber-800',
      EXHAUSTED: 'bg-red-100 text-red-800',
    } as Record<string, string>
  )[status] || 'bg-gray-100 text-gray-800'
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}
</script>
